import { useId, useRef, useState, type KeyboardEvent } from 'react'
import { FiCheck, FiChevronDown } from 'react-icons/fi'
import { useClickOutside } from '@/hooks'
import { cn } from '@/lib/cn'

export interface SelectProps {
  id?: string
  name?: string
  label?: string
  options: string[]
  defaultValue?: string
  placeholder?: string
  className?: string
}

export const Select = ({
  id,
  name,
  label,
  options,
  defaultValue,
  placeholder = 'Select an option',
  className,
}: SelectProps) => {
  const reactId = useId()
  const selectId = id ?? reactId
  const listId = `${selectId}-listbox`

  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(defaultValue ?? '')
  const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.indexOf(value)))
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useClickOutside(rootRef, () => setOpen(false))

  const commit = (index: number) => {
    const option = options[index]
    if (option === undefined) return
    setValue(option)
    setActiveIndex(index)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        setActiveIndex(Math.max(0, options.indexOf(value)))
        setOpen(true)
      }
      return
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        break
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, options.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        break
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        event.preventDefault()
        setActiveIndex(options.length - 1)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(activeIndex)
        break
      default:
        break
    }
  }

  return (
    <div ref={rootRef} className="relative flex flex-col gap-3">
      {label && (
        <label htmlFor={selectId} className="text-sm font-medium text-ibon-dark">
          {label}
        </label>
      )}

      <input type="hidden" name={name} value={value} />

      <button
        ref={buttonRef}
        id={selectId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${selectId}-option-${activeIndex}` : undefined}
        onClick={() => {
          setActiveIndex(Math.max(0, options.indexOf(value)))
          setOpen((prev) => !prev)
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-14 w-full items-center justify-between rounded-none border border-border bg-white px-4 text-left text-ibon-dark transition-colors',
          'focus:border-ibon-dark focus:outline-none',
          open && 'border-ibon-dark',
          className,
        )}
      >
        <span className={value ? 'text-ibon-dark' : 'text-text-muted'}>{value || placeholder}</span>
        <FiChevronDown
          className={cn('shrink-0 text-ibon-sky transition-transform', open && 'rotate-180')}
          size={18}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={selectId}
          tabIndex={-1}
          className="absolute left-0 top-full z-20 mt-2 max-h-64 w-full overflow-auto border border-border bg-white shadow-lg"
        >
          {options.map((option, index) => (
            <li
              key={option}
              id={`${selectId}-option-${index}`}
              role="option"
              aria-selected={option === value}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => commit(index)}
              className={cn(
                'flex cursor-pointer items-center justify-between px-4 py-3 text-sm text-ibon-dark transition-colors',
                index === activeIndex && 'bg-ibon-sky/10',
                option === value && 'font-medium',
              )}
            >
              {option}
              {option === value && <FiCheck className="text-ibon-sky" size={16} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
